import * as React from 'react';

// Type-only import so the `{@link InputGroup}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type InputGroup } from './InputGroup';
import { cn } from './utils/cn';

/**
 * Single-line text input. The base form control for any value the user types.
 *
 * For inputs that need a leading icon, trailing button, or other addon, wrap
 * with {@link InputGroup} instead.
 *
 * @example
 * <Input
 *   type="email"
 *   placeholder="patient@example.com"
 *   onChange={(e) => setEmail(e.currentTarget.value)}
 * />
 */
function Input({
  className,
  type,
  size = 'md',
  ...props
}: Omit<React.ComponentProps<'input'>, 'size'> & { size?: 'md' | 'lg' }) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        'w-full min-w-0 border border-input bg-bg-primary py-1 transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
        size === 'lg'
          ? 'h-12 rounded-lg px-4 text-sm'
          : 'h-10 rounded-md px-2.5 text-sm',
        className,
      )}
      {...props}
    />
  );
}

export { Input };
